/**
 * Scheduling Service Constants
 *
 * Provider domains and prefill processors for scheduling link integration.
 */

import type {
  SchedulingLinkPrefillDataI,
  PrefillProcessor,
} from "../interfaces/scheduling";

// Scheduling service provider domains
export const SCHEDULING_PROVIDERS = {
  CALENDLY: "calendly.com",
  CAL_COM: "cal.com",
  // Base domain, not "meetings.hubspot.com": HubSpot serves meeting links from
  // regional hosts too (meetings-na2, meetings-eu1, ...) which the exact host
  // never matched. All HubSpot booking hosts end in ".hubspot.com".
  HUBSPOT: "hubspot.com",
  CHILI_PIPER: "chilipiper.com",
  SAVVY_CAL: "savvycal.com",
  ACUITY: { domain: "acuityscheduling.com", altDomains: ["as.me"] },
  ZCAL: "zcal.co",
  YOU_CAN_BOOK_ME: "youcanbook.me",
  APPOINTLET: { domain: "appointlet.com", altDomains: ["appt.link"] },
  TIDY_CAL: "tidycal.com",
} as const;

/**
 * Providers whose PUBLIC booking page pre-adds a guest email via a URL query
 * param. Verified against provider docs (2026-07): only these three expose it —
 * Calendly & Zcal take a comma-joined `guests=`, cal.com takes a repeated
 * `guests=`. Every other provider's booking page prefills only the primary
 * booker, so the lookalike guest must be added manually there. Single source of
 * truth for both the processors below and `schedulingLinkSupportsGuests`.
 */
export const GUEST_URL_CAPABLE_DOMAINS: readonly string[] = [
  SCHEDULING_PROVIDERS.CALENDLY,
  SCHEDULING_PROVIDERS.CAL_COM,
  SCHEDULING_PROVIDERS.ZCAL,
];

const splitName = (
  fullName: string
): { firstName: string; lastName: string } => {
  const parts = fullName.trim().split(/\s+/);

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const getFullName = (data: SchedulingLinkPrefillDataI): string => {
  if (data.name) {
    return data.name;
  }

  if (data.firstName || data.lastName) {
    return [data.firstName, data.lastName].filter(Boolean).join(" ");
  }

  return "";
};

const getNameParts = (
  data: SchedulingLinkPrefillDataI
): { firstName: string; lastName: string } => {
  if (data.firstName !== undefined || data.lastName !== undefined) {
    return {
      firstName: data.firstName || "",
      lastName: data.lastName || "",
    };
  }

  if (data.name) {
    return splitName(data.name);
  }

  return { firstName: "", lastName: "" };
};

/**
 * Appends guest emails to a booking URL. Only the two providers below expose
 * a documented guest query param; the rest have none, so we never emit one for
 * them (a bogus param is silently dropped at best, breaks the link at worst).
 *   - "repeat": one `?guests=a&guests=b` param per guest (cal.com).
 *   - "csv":    a single `?guests=a,b` comma-joined param (Calendly).
 */
const appendGuests = (
  url: URL,
  guests: string[] | undefined,
  mode: "repeat" | "csv"
): void => {
  const list = (guests ?? []).filter(Boolean);

  if (list.length === 0) {
    return;
  }

  if (mode === "repeat") {
    list.forEach((guest) => url.searchParams.append("guests", guest));

    return;
  }

  url.searchParams.set("guests", list.join(","));
};

export const SCHEDULING_SERVICE_PROCESSORS: Record<string, PrefillProcessor> = {
  // Calendly: name, email, guests (comma-separated; requires "invitees can add
  // guests" on the event type).
  // @see https://help.calendly.com/hc/en-us/articles/226766767
  [SCHEDULING_PROVIDERS.CALENDLY]: (url, data) => {
    const fullName = getFullName(data);

    if (fullName) {
      url.searchParams.set("name", fullName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    appendGuests(url, data.guests, "csv");

    return url;
  },

  // Cal.com: name, email, guests (one repeated param per guest).
  // @see https://cal.com/help/bookings/prefill-fields
  [SCHEDULING_PROVIDERS.CAL_COM]: (url, data) => {
    const fullName = getFullName(data);

    if (fullName) {
      url.searchParams.set("name", fullName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    appendGuests(url, data.guests, "repeat");

    return url;
  },

  // HubSpot Meetings: firstname, lastname, email
  // @see https://community.hubspot.com/t5/Sales-Hub-Tools/Add-parameters-to-meetings-link/m-p/304947
  [SCHEDULING_PROVIDERS.HUBSPOT]: (url, data) => {
    const { firstName, lastName } = getNameParts(data);

    if (firstName) {
      url.searchParams.set("firstname", firstName);
    }

    if (lastName) {
      url.searchParams.set("lastname", lastName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    return url;
  },

  // Chili Piper: personfirstname, personlastname, personemail
  // @see https://help.chilipiper.com/hc/en-us/articles/360053021614
  [SCHEDULING_PROVIDERS.CHILI_PIPER]: (url, data) => {
    const { firstName, lastName } = getNameParts(data);

    if (firstName) {
      url.searchParams.set("personfirstname", firstName);
    }

    if (lastName) {
      url.searchParams.set("personlastname", lastName);
    }

    if (data.email) {
      url.searchParams.set("personemail", data.email);
    }

    return url;
  },

  // SavvyCal: display_name, email
  // @see https://docs.savvycal.com/article/14-personalizing-links
  [SCHEDULING_PROVIDERS.SAVVY_CAL]: (url, data) => {
    const fullName = getFullName(data);

    if (fullName) {
      url.searchParams.set("display_name", fullName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    return url;
  },

  // Acuity Scheduling: firstName, lastName, email
  // @see https://help.acuityscheduling.com/hc/en-us/articles/31919067234445
  [SCHEDULING_PROVIDERS.ACUITY.domain]: (url, data) => {
    const { firstName, lastName } = getNameParts(data);

    if (firstName) {
      url.searchParams.set("firstName", firstName);
    }

    if (lastName) {
      url.searchParams.set("lastName", lastName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    return url;
  },

  // Zcal: name, email, guests (comma-separated; guest fields must be enabled).
  // @see https://help.zcal.co/invite-settings/pre-populate-form-fields
  [SCHEDULING_PROVIDERS.ZCAL]: (url, data) => {
    const fullName = getFullName(data);

    if (fullName) {
      url.searchParams.set("name", fullName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    appendGuests(url, data.guests, "csv");

    return url;
  },

  // YouCanBookMe: FNAME, LNAME, EMAIL (uppercase required)
  // @see https://support.youcanbook.me/hc/en-us/articles/17321940609687
  [SCHEDULING_PROVIDERS.YOU_CAN_BOOK_ME]: (url, data) => {
    const { firstName, lastName } = getNameParts(data);

    if (firstName) {
      url.searchParams.set("FNAME", firstName);
    }

    if (lastName) {
      url.searchParams.set("LNAME", lastName);
    }

    if (data.email) {
      url.searchParams.set("EMAIL", data.email);
    }

    return url;
  },

  // Appointlet: field__first-name, field__last-name, email
  // @see http://kb.appointlet.help/en/articles/5031004
  [SCHEDULING_PROVIDERS.APPOINTLET.domain]: (url, data) => {
    const { firstName, lastName } = getNameParts(data);

    if (firstName) {
      url.searchParams.set("field__first-name", firstName);
    }

    if (lastName) {
      url.searchParams.set("field__last-name", lastName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    return url;
  },

  // TidyCal: name, email
  // @see https://x.com/Tidy_Cal/status/1636034862661394434
  [SCHEDULING_PROVIDERS.TIDY_CAL]: (url, data) => {
    const fullName = getFullName(data);

    if (fullName) {
      url.searchParams.set("name", fullName);
    }

    if (data.email) {
      url.searchParams.set("email", data.email);
    }

    return url;
  },
};

// Add alt domain mappings
SCHEDULING_SERVICE_PROCESSORS[SCHEDULING_PROVIDERS.ACUITY.altDomains[0]] =
  SCHEDULING_SERVICE_PROCESSORS[SCHEDULING_PROVIDERS.ACUITY.domain];
SCHEDULING_SERVICE_PROCESSORS[SCHEDULING_PROVIDERS.APPOINTLET.altDomains[0]] =
  SCHEDULING_SERVICE_PROCESSORS[SCHEDULING_PROVIDERS.APPOINTLET.domain];
